/** Suggestions a person hid, per browser (localStorage), so a folder offered by mistake stays gone. */
import { useMemo } from 'react';
import { create } from 'zustand';
import { suggestionKey } from '@orgus/protocol';
import type { ProjectSuggestion } from '@orgus/protocol';
import { useSuggestions } from '../../lib/store.ts';
import { visibleSuggestions } from './model.ts';

const KEY = 'fd.projects.hiddenSuggestions';
/** Enough for any real machine; the oldest drop off first. */
const MAX = 200;

function load(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(-MAX) : [];
  } catch {
    return [];
  }
}

function save(keys: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(keys));
  } catch {
    // private mode: hidden for this tab only
  }
}

interface HiddenStore {
  keys: string[];
  hide: (s: Pick<ProjectSuggestion, 'machineId' | 'path'>) => void;
  unhide: (s: Pick<ProjectSuggestion, 'machineId' | 'path'>) => void;
}

export const useHiddenSuggestions = create<HiddenStore>()((set, get) => ({
  keys: typeof localStorage === 'undefined' ? [] : load(),
  hide: (s) => {
    const k = suggestionKey(s);
    const keys = [...get().keys.filter((x) => x !== k), k].slice(-MAX);
    save(keys);
    set({ keys });
  },
  unhide: (s) => {
    const k = suggestionKey(s);
    const keys = get().keys.filter((x) => x !== k);
    save(keys);
    set({ keys });
  },
}));

/** The suggestions to offer: no scratch folders, none a person hid. */
export function useVisibleSuggestions(): ProjectSuggestion[] {
  const all = useSuggestions();
  const keys = useHiddenSuggestions((s) => s.keys);
  return useMemo(() => visibleSuggestions(all, new Set(keys)), [all, keys]);
}
